import { p as proxyCustomElement, H, h, a as Host } from './index.js';
import { d as dateFnsExports } from './p-iWCgRMLC.js';
import { i as isDefined } from './p-D_OOeJgV.js';
import { d as defineCustomElement$5 } from './p-DvxzXrZp.js';
import { d as defineCustomElement$4 } from './p-C78HRAnj.js';
import { d as defineCustomElement$3 } from './p-xjpL7DAY.js';
import { d as defineCustomElement$2 } from './p-Cr5d_5Z4.js';
import { d as defineCustomElement$1 } from './p-CZA0kb6w.js';

function memoize(fn, options = {}) {
    const { cache = new Map(), getCacheKey } = options;
    const memoizedFn = function (arg) {
        const key = getCacheKey ? getCacheKey(arg) : arg;
        if (cache.has(key)) {
            return cache.get(key);
        }
        const result = fn.call(this, arg);
        cache.set(key, result);
        return result;
    };
    memoizedFn.cache = cache;
    return memoizedFn;
}

//#region src/module/format/date/index.ts
const toDate = (input) => {
	if (typeof input === "undefined") return void 0;
	if (input === null) return null;
	if (input instanceof Date) return input;
	if (!input) return void 0;
	if (isValidDateString(input)) {
		const [year, month, day] = input.split("-").map((i) => Number.parseInt(i, 10));
		return new Date(year, month - 1, day);
	}
	throw new Error("Invalid date string");
};
/**
* Convert Date or ISOString to date string: yyyy-mm-dd
*
* When passing `Date`: always uses local TZ, not UTC!
*/
function toDateString(input) {
	if (typeof input === "undefined") return void 0;
	if (input === null) return null;
	if (typeof input === "string") {
		if (!input) return void 0;
		if (!(isValidDateString(input) || isValidDateTimeString(input))) throw new Error("Invalid date(time) string");
		return input.substring(0, 10);
	}
	return [
		input.getFullYear(),
		input.getMonth() + 1,
		input.getDate()
	].map((v) => `${v}`.padStart(2, "0")).join("-");
}
function isValidDateTimeString(input) {
	if (typeof input === "undefined") return false;
	let [date, time] = input.split("T");
	time = time?.replace(/(Z)|((\+|-)\d{2}:\d{2})$/gm, "");
	return isValidDateString(date) && isValidTimeString(time);
}
function isValidDateString(input) {
	if (typeof input !== "string") return false;
	const parts = input.split("-");
	if (parts.length !== 3) return false;
	const [y, m, d] = parts;
	if (!(typeof y === "string" && typeof m === "string" && typeof d === "string")) return false;
	if (!(y.length === 4 && +y > 0)) return false;
	if (!(m.length === 2 && +m > 0 && +m <= 12)) return false;
	if (!(d.length === 2 && +d > 0 && +d <= 31)) return false;
	return true;
}
function isValidTimeString(input) {
	if (typeof input !== "string") return false;
	if (input.match(/(Z)|((\+|-)\d{2}:\d{2})$/gm) !== null) return false;
	const parts = input.split(":");
	if (!((p) => {
		return p.length === 2 || p.length === 3;
	})(parts)) return false;
	const [h, m, _s] = parts;
	if (!(h.length === 2 && +h < 24)) return false;
	if (!(m.length === 2 && +m < 60)) return false;
	if (_s) {
		const [s, ms] = _s.split(".");
		if (!(s.length === 2 && +s < 60)) return false;
		if (ms) {
			if (!(ms.length === 3 && +ms < 1e3)) return false;
		}
	}
	return true;
}

const FORMATS = {
    // Friendly: Short (June 18, 2019)
    FRIENDLY_SHORT: 'MMM dd, yyyy',
    // Friendly: Medium (Tuesday, June 18, 2019)
    FRIENDLY_MEDIUM: 'iiii, MMMM d, yyyy',
    // ISO 8601: DateTime (2019-06-18T00:00:00+00:00)
    ISO_FULL: 'yyyy-MM-dd[T]HH:mm:ssZ',
    // ISO 8601: Date (2019-06-18)
    ISO_DAY: 'yyyy-MM-dd',
    // Calendar: Day (18)
    CAL_DAY: 'dd',
    // Calendar: Header (month + year) (June 2019)
    CAL_MONTH_YEAR: 'MMMM yyyy',
    DEBUG: 'ddd, dd MMM yyyy HH:mm:ss z',
};
const format = (type, date) => dateFnsExports.format(date, FORMATS[type]);

const DAYS_IN_WEEK = 7;
//
// Since we're dealing with dates, time of day is irrelevant.
// That's why we're wrapping the following date-fns methods
// to include `isSameDay`.
//
const isBefore = (date, compareDate) => {
    if (dateFnsExports.isSameDay(date, compareDate)) {
        return false;
    }
    return dateFnsExports.isBefore(date, compareDate);
};
const isAfter = (date, compareDate) => {
    if (dateFnsExports.isSameDay(date, compareDate)) {
        return false;
    }
    return dateFnsExports.isAfter(date, compareDate);
};
const isWithinInterval = (date, { start, end }) => {
    if (dateFnsExports.isSameDay(date, start) || dateFnsExports.isSameDay(date, end)) {
        return true;
    }
    try {
        // Return within range including same days
        return dateFnsExports.isWithinInterval(date, { start, end });
    }
    catch {
        return false;
    }
};
/**
 * Get array of day objects for month with every week filled to 7 days, even when days are not in the same month.
 *
 * Also accepts passing a string for easy function memoization. String will be parsed by date-fns.
 *
 * @example
 * // May 2018
 * [
 *  { inMonth: false, date: Date('Sunday April 29') },
 *  { inMonth: false, date: Date('Monday April 30') },
 *  { inMonth: true, date: Date('Tuesday May 1') },
 *  ...
 *  { inMonth: true, date: Date('Thurday May 31') },
 *  { inMonth: false, date: Date('Friday June 1') },
 *  { inMonth: false, date: Date('Saturday June 2') }
 * ]
 */
function getCalendarDays({ year, month, weekStartsOn, }) {
    const date = new Date(year, month);
    if (!dateFnsExports.isValid(date)) {
        throw new Error(`Invalid date \`${date}\`!`);
    }
    // Get the first and last days of the month
    const monthStartDate = dateFnsExports.startOfMonth(date);
    const monthEndDate = dateFnsExports.endOfMonth(date);
    // If the first of the month is Tuesday May 1 then the first day of that week is Sunday April 29
    const startDate = dateFnsExports.startOfWeek(monthStartDate, { weekStartsOn });
    // If the last day of the month is Thursday May 31 then the last day of that week is Saturday June 2
    const endDate = dateFnsExports.endOfWeek(monthEndDate, { weekStartsOn });
    const ret = [];
    let d = startDate;
    while (
    // Use a fixed number instead of while true
    ret.length < 100) {
        // Stop adding to ret when we've surpassed last weekday in month
        if (d > endDate) {
            break;
        }
        ret.push({
            inMonth: dateFnsExports.isSameMonth(d, monthStartDate),
            date: d,
            key: +`${d.getFullYear()}${d.getMonth()}${d.getDate()}`,
            formatted: {
                full: format('FRIENDLY_MEDIUM', d),
                day: format('CAL_DAY', d),
            },
        });
        d = dateFnsExports.addDays(d, 1);
    }
    return ret;
}
// Memoize function to return the same array references for each month.
// This will improve change detection and avoid unneeded renders when arrays are identical.
const memoizedGetCalendarDays = memoize(getCalendarDays, {
    getCacheKey(args) {
        return [args.year, args.month, args.weekStartsOn].join(';');
    },
});
const getWeekDayHeaders = (weekStartsOn) => {
    switch (weekStartsOn) {
        case 0:
            // Week starts on Sunday
            return ['S', 'M', 'Tu', 'W', 'Th', 'F', 'S'];
        default:
            // Week starts on Monday (ISO 8601)
            return ['M', 'Tu', 'W', 'Th', 'F', 'S', 'S'];
    }
};
/**
 * Determine whether day is within a range.
 *
 * Note: same day allowed!
 */
function isDateInRange(date, props) {
    // We need one of these to be able to compare a range
    if (!(props.rangeStartDate || props.rangeEndDate)) {
        return false;
    }
    // We disable ranges when there is an error and we're not hovering
    if (props.dateRangeError && !props.hoverDate) {
        return false;
    }
    // The compare date is either what we're hovering on, or the current value
    const compareDate = props.hoverDate || (props.value ? toDate(props.value) : undefined);
    if (!compareDate) {
        return false;
    }
    // If we have a fixed rangeEndDate, we're setting the range start
    const compareDateIsStart = !!props.rangeEndDate;
    // If we have a fixed rangeStartDate, we're setting the range end
    const compareDateIsEnd = !!props.rangeStartDate;
    // Date must be between compareDate (start) and end
    const rangeEndDate = props.rangeEndDate ? toDate(props.rangeEndDate) : undefined;
    if (compareDateIsStart && rangeEndDate && isWithinInterval(date, { start: compareDate, end: rangeEndDate })) {
        return true;
    }
    // Date must be between start and compareDate (end)
    const rangeStartDate = props.rangeStartDate ? toDate(props.rangeStartDate) : undefined;
    if (compareDateIsEnd && rangeStartDate && isWithinInterval(date, { start: rangeStartDate, end: compareDate })) {
        return true;
    }
    // When selecting start, any date after the end can not be in range
    if (compareDateIsStart && rangeEndDate && isAfter(date, rangeEndDate)) {
        return false;
    }
    // When selecting end, any date before the start can not be in range
    if (compareDateIsEnd && rangeStartDate && isBefore(date, rangeStartDate)) {
        return false;
    }
    // Same day also falls in range
    if (dateFnsExports.isSameDay(date, compareDate)) {
        return true;
    }
    return false;
}
/**
 * Determine whether a given date is disabled based on min and/or max date combined with range start and/or end date.
 *
 * Note: same day is allowed!
 */
function isDateDisabled(date, props) {
    // If date is before minDate
    const min = props.min ? toDate(props.min) : undefined;
    if (min && isBefore(date, min)) {
        return true;
    }
    // If rangeStartDate is valid and date is before rangeStartDate
    const rangeStartDate = props.rangeStartDate ? toDate(props.rangeStartDate) : undefined;
    if (rangeStartDate && isBefore(date, rangeStartDate)) {
        return true;
    }
    // If date is after maxDate
    const max = props.max ? toDate(props.max) : undefined;
    if (max && isAfter(date, max)) {
        return true;
    }
    // If rangeEndDate is valid and date is after rangeEndDate
    const rangeEndDate = props.rangeEndDate ? toDate(props.rangeEndDate) : undefined;
    if (rangeEndDate && isAfter(date, rangeEndDate)) {
        return true;
    }
    return false;
}
/**
 * Determine whether navigation to previous month is disabled.
 *
 * It will only be disabled if there is NO more available date to select before
 * the currently displayed month. This effectively means any month before value,
 * rangeStartDate or minDate.
 */
function isPrevMonthDisabled(displayDate, props) {
    // If there's a min - determined by either a static range start or min date - it makes no sense
    // to allow further navigation
    if (props.min || props.rangeStartDate) {
        // Get the earliest date of one of these three
        const firstPossibleDate = dateFnsExports.min([props.value || undefined, props.rangeStartDate, props.min].filter(isDefined).map(toDate));
        // Enable navigation if there's a valid date before the currently displayed month
        if (dateFnsExports.isValid(firstPossibleDate) && isBefore(firstPossibleDate, dateFnsExports.startOfMonth(displayDate))) {
            return false;
        }
        return true;
    }
    // Don't disable anything by default
    return false;
}
/**
 * Determine whether navigation to next month is disabled.
 *
 * It will only be disabled if there is NO more available date to select after
 * the currently displayed month. This effectively means any month after value,
 * rangeEndDate or maxDate.
 *
 */
function isNextMonthDisabled(displayDate, props) {
    // If there's a max - determined by either a static range end or max date - it makes no sense
    // to allow further navigation
    if (props.max || props.rangeEndDate) {
        // Get the latest date of one of these three
        const lastPossibleDate = dateFnsExports.max([props.value || undefined, props.rangeEndDate, props.max].filter(isDefined).map(toDate));
        // Enable navigation if there's a valid date after the currently displayed month
        if (dateFnsExports.isValid(lastPossibleDate) && isAfter(lastPossibleDate, dateFnsExports.endOfMonth(displayDate))) {
            return false;
        }
        return true;
    }
    // Don't disable anything by default
    return false;
}
/**
 * Determine whether a date is the start of a date range.
 */
function isDateRangeStart(date, props) {
    // Prevent start date from being later than the end date
    const rangeEndDate = props.rangeEndDate ? toDate(props.rangeEndDate) : undefined;
    if (rangeEndDate && isAfter(date, rangeEndDate)) {
        return false;
    }
    const compareDate = 
    // Fixed range start takes precedence
    props.rangeStartDate ||
        // If there's no fixed range start, there may be a fixed end so try hover date
        (props.hoverDate ? toDateString(props.hoverDate) : undefined) ||
        // If there's no hover date, try current value
        props.value;
    return compareDate ? toDateString(date) === compareDate : false;
}
/**
 * Determine whether a date is the end of a date range.
 */
function isDateRangeEnd(date, props) {
    // Prevent end date from being earlier than the start date
    const rangeStartDate = props.rangeStartDate ? toDate(props.rangeStartDate) : undefined;
    if (rangeStartDate && isBefore(date, rangeStartDate)) {
        return false;
    }
    const compareDate = 
    // Fixed range end takes precedence
    props.rangeEndDate ||
        // If there's no fixed range end, there may be a fixed start so try hover date
        (props.hoverDate ? toDateString(props.hoverDate) : undefined) ||
        // If there's no hover date, try current value
        props.value;
    return compareDate ? toDateString(date) === compareDate : false;
}
function getInitialDisplayDate(props) {
    if (props.initialCalendarDisplayDate === 'selected') {
        const valueDate = props.value ? toDate(props.value) : undefined;
        if (valueDate) {
            // Return existing value
            return valueDate;
        }
    }
    // Return local start of day
    return dateFnsExports.startOfDay(new Date());
}
function checkDateOverlaps(props) {
    const valueDate = props.value ? toDate(props.value) : undefined;
    const rangeStartDate = props.rangeStartDate ? toDate(props.rangeStartDate) : undefined;
    if (valueDate && rangeStartDate) {
        // Treat value as range end
        if (isBefore(valueDate, rangeStartDate)) {
            throw new Error('Range end may not be before range start.');
        }
    }
    const rangeEndDate = props.rangeEndDate ? toDate(props.rangeEndDate) : undefined;
    if (valueDate && rangeEndDate) {
        // Treat value as range start
        if (isAfter(valueDate, rangeEndDate)) {
            throw new Error('Range start may not be after range end.');
        }
    }
    const max = props.max ? toDate(props.max) : undefined;
    if (valueDate && max) {
        if (isAfter(valueDate, max)) {
            throw new Error('Value may not be after max date.');
        }
    }
    const min = props.min ? toDate(props.min) : undefined;
    if (valueDate && min) {
        if (isBefore(valueDate, min)) {
            throw new Error('Value may not be before min date.');
        }
    }
}
function segmentMonthInWeeks(month) {
    return Array.from({ length: Math.ceil(month.length / DAYS_IN_WEEK) }, (_, index) => month.slice(index * DAYS_IN_WEEK, (index + 1) * DAYS_IN_WEEK));
}

const dateInputCalendarCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;align-items:flex-start;background-color:var(--jw-theme-surface-elevated-default-bg-color);box-sizing:border-box;display:inline-flex;flex-direction:column;font-family:Open Sans;font-size:.875rem;font-weight:400;gap:.25rem;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;padding:1rem}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.month-header{align-items:center;color:var(--jw-theme-surface-text-default-color);font-size:.875rem;justify-content:space-between;letter-spacing:-.0004em;line-height:1.25rem;width:14rem}.month-header,.weekdays-header{display:flex;font-family:Open Sans;font-weight:600}.weekdays-header{font-size:.75rem;letter-spacing:0;line-height:1rem}.weekday-header{align-items:center;color:var(--jw-theme-surface-text-helper-color);display:inline-flex;height:2rem;justify-content:center;width:2rem}.days-container{align-items:flex-start;border-radius:.25rem;display:flex}.day-btn{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;align-items:center;background:none;border:none;box-sizing:border-box;color:var(--jw-theme-surface-text-default-color);cursor:pointer;display:inline-flex;font-family:Open Sans;font-size:.875rem;font-weight:inherit;font-weight:400;height:2rem;justify-content:center;letter-spacing:-.0004em;line-height:normal;line-height:1.25rem;margin:0;padding:0;position:relative;-webkit-user-select:none;user-select:none;width:2rem;z-index:2}.day-btn *,.day-btn :after,.day-btn :before{box-sizing:inherit}.day-btn img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.day-btn:before{background-color:var(--jw-theme-surface-item-active-bg-color);transition:opacity .15s ease-in;z-index:-2}.day-btn:after,.day-btn:before{content:\"\";height:100%;opacity:0;position:absolute;right:0;top:0;width:100%}.day-btn:after{background-color:var(--jw-theme-system-primary-default-bg-color);border-radius:.25rem;transform:scale(.8);transition:transform .15s ease;z-index:-1}.day-btn[data-outside-month]:enabled{color:var(--jw-theme-surface-text-helper-color)}.day-btn[data-outside-month]:enabled[data-selected],.day-btn[data-range-end],.day-btn[data-range-start],.day-btn[data-selected]{color:var(--jw-theme-system-primary-default-color)}.day-btn[data-range-end]:after,.day-btn[data-range-start]:after,.day-btn[data-selected]:after{opacity:1;transform:scale(1)}.day-btn[data-range-end][data-error],.day-btn[data-range-start][data-error],.day-btn[data-selected][data-error]{color:var(--jw-theme-form-validation-error-color)}.day-btn[data-range-end][data-error]:after,.day-btn[data-range-start][data-error]:after,.day-btn[data-selected][data-error]:after{opacity:0;transform:scale(0)}.day-btn[data-in-range]:not([data-range-start]):not([data-range-end]):before{opacity:1}.day-btn[data-in-range]:not([data-range-start]):not([data-range-end]):not([data-selected]):first-child:before{border-radius:.25rem 0 0 .25rem}.day-btn[data-in-range]:not([data-range-start]):not([data-range-end]):not([data-selected]):last-child:before{border-radius:0 .25rem .25rem 0}.day-btn[data-in-range][data-range-start]:not([data-range-end]):before{border-radius:.25rem 0 0 .25rem;opacity:1}.day-btn[data-in-range][data-range-end]:not([data-range-start]):before{border-radius:0 .25rem .25rem 0;opacity:1}.day-btn:hover:not([data-selected]):not([disabled]){color:var(--jw-theme-surface-item-active-color)}.day-btn:hover:not([data-selected]):not([disabled]):after{background-color:initial;border-radius:.25rem;opacity:1;outline:1px solid var(--jw-theme-form-border-regular-focus-border-color);outline-offset:-1px;transform:scale(1)}.day-btn:focus{outline:none}.day-btn[disabled]{color:var(--jw-theme-form-field-disabled-inverted-bg-color);cursor:not-allowed}.day-indicator{bottom:-4px;left:0;position:absolute;right:0}";

const WuiDateInputCalendar = /*@__PURE__*/ proxyCustomElement(class WuiDateInputCalendar extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        /**
         * If disabled
         */
        this.disabled = false;
        /**
         * First day of week
         *
         * 0 = Sunday
         * 1 = Monday
         */
        this.weekStartsOn = 0;
        this.initialCalendarDisplayDate = 'today';
        this.dateRangeError = false;
        this.displayDate = getInitialDisplayDate(this);
        this.isDateDisabled = (date) => this.disabled || isDateDisabled(date, this);
        this.isPrevMonthDisabled = () => this.disabled || isPrevMonthDisabled(this.displayDate, this);
        this.isNextMonthDisabled = () => this.disabled || isNextMonthDisabled(this.displayDate, this);
        this.goToPrevMonth = () => {
            this.displayDate = dateFnsExports.subMonths(this.displayDate, 1);
        };
        this.goToNextMonth = () => {
            this.displayDate = dateFnsExports.addMonths(this.displayDate, 1);
        };
        this.selectDate = (date) => {
            this.value = toDateString(date);
            this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
        };
        this.handleMouseEnter = (date) => {
            // Prevents range start to flicker when hovering over a disabled date.
            if (!this.isDateDisabled(date)) {
                this.hoverDate = date;
            }
        };
        this.handleMouseLeave = () => {
            this.hoverDate = undefined;
        };
        this.getDayAttributes = (date, inMonth) => {
            const isSelected = this.value && toDateString(date) === this.value;
            return {
                class: 'day-btn',
                'data-range-start': isDateRangeStart(date, this),
                'data-range-end': isDateRangeEnd(date, this),
                'data-in-range': isDateInRange(date, this),
                'data-outside-month': !inMonth,
                'data-selected': isSelected,
                'data-error': this.dateRangeError && isSelected,
            };
        };
    }
    _value(newValue) {
        const date = newValue ? toDate(newValue) : undefined;
        // Only set display date if there's a valid date
        if (date) {
            this.displayDate = date;
        }
    }
    checkSanity() {
        // Let developer know when something is wrong with their implementation
        if (this.rangeStartDate && this.rangeEndDate) {
            throw new Error('Can not set both `rangeStartDate` and `rangeEndDate`!');
        }
        const minDate = this.min ? toDate(this.min) : undefined;
        const maxDate = this.max ? toDate(this.max) : undefined;
        if (minDate && maxDate && isAfter(minDate, maxDate)) {
            throw new Error('`minDate` can not be after `maxDate`!');
        }
        if (maxDate && minDate && isBefore(maxDate, minDate)) {
            throw new Error('`maxDate` can not be before `minDate`!');
        }
        try {
            checkDateOverlaps(this);
            this.dateRangeError = false;
        }
        catch {
            this.dateRangeError = true;
        }
    }
    componentWillRender() {
        this.checkSanity();
    }
    componentWillLoad() {
        this.displayDate = getInitialDisplayDate(this);
    }
    render() {
        const weekStartsOn = typeof this.weekStartsOn === 'undefined' ? 0 : this.weekStartsOn;
        const calendarDays = memoizedGetCalendarDays({
            year: this.displayDate.getFullYear(),
            month: this.displayDate.getMonth(),
            weekStartsOn,
        });
        const weekdays = getWeekDayHeaders(weekStartsOn);
        const weeks = segmentMonthInWeeks(calendarDays);
        return (h(Host, { key: 'cb75aae528ec3a83473b7b0edb9ff48f3d2f29b1' }, h("input", { key: '4c193cc727d5e4efc9cd7eb6f884a46de61d503f', hidden: true, type: "date", value: this.value, name: this.name, onChange: (e) => {
                // Don't bubble this event up, use `input` instead
                e.stopPropagation();
            } }), h("div", { key: '29bfca1efc2e490a6463ec12a19e832c32221358', class: "month-header" }, h("wui-button", { key: '50872563b0dbb9c63dfba8bc7450ebf1970e2c4e', icon: "chevron-left", type: "icon", tooltip: "Previous month", disabled: this.isPrevMonthDisabled(), onClick: this.goToPrevMonth }), h("span", { key: '472db4f76450525984711e818e3392b9ed816a93' }, format('CAL_MONTH_YEAR', this.displayDate)), h("wui-button", { key: '22b507d71b35c9cd206016b1b9ad3fdef0623e12', icon: "chevron-right", type: "icon", tooltip: "Next month", disabled: this.isNextMonthDisabled(), onClick: this.goToNextMonth })), h("div", { key: '797808ac1c63a79962fe620dc334760aa00a7492', class: "weekdays-header" }, weekdays.map((weekday, key) => (h("div", { key: key, class: "weekday-header" }, weekday)))), weeks.map((week, index) => (h("div", { key: `${this.displayDate}-week-${index}`, class: "days-container" }, week.map((day) => {
            const isToday = dateFnsExports.isSameDay(day.date, new Date());
            return (h("button", { key: day.key, title: day.formatted.full, disabled: this.isDateDisabled(day.date), onMouseEnter: () => this.handleMouseEnter(day.date), onMouseLeave: this.handleMouseLeave, onClick: () => this.selectDate(day.date), type: "button", ...this.getDayAttributes(day.date, day.inMonth) }, day.formatted.day, isToday && h("span", { class: "day-indicator" }, "\u2013")));
        }))))));
    }
    get elm() { return this; }
    static get watchers() { return {
        "value": ["_value"]
    }; }
    static get style() { return dateInputCalendarCss; }
}, [257, "wui-date-input-calendar", {
        "disabled": [4],
        "weekStartsOn": [2, "week-starts-on"],
        "value": [1025],
        "name": [1],
        "rangeStartDate": [1, "range-start-date"],
        "rangeEndDate": [1, "range-end-date"],
        "min": [1],
        "max": [1],
        "initialCalendarDisplayDate": [1, "initial-calendar-display-date"],
        "dateRangeError": [32],
        "hoverDate": [32],
        "displayDate": [32]
    }, undefined, {
        "value": ["_value"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-date-input-calendar", "wui-button", "wui-icon", "wui-loader", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-date-input-calendar":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiDateInputCalendar);
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiDateInputCalendar as W, defineCustomElement as d, isValidDateString as i, toDateString as t };
